import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  inject,
  Input,
  OnInit,
} from '@angular/core';
import { assert, isDefined } from '@sama/js-core';
import { DirectiveSuperclass } from '@sama/ng-core';
import { firstValueFrom, Observable, timer } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  fusionStoreToken,
  TaskWorkspaceStore,
} from '../../../../../../../core/h3ml/state/task-workspace-store';
import { FusionManager } from '../../../../../../../core/outputs/annotation/annotation-shared/fusion/fusion-manager';
import { SelectionService } from '../../../../../../../core/outputs/annotation/annotation-shared/utils/selection.service';
import { ShapeListContextMenuService } from '../../../../../../../core/shared/context-menu/shape-list-context-menu.service';
import { FeatureFlagService } from '../../../../../../../core/shared/misc-services/feature-flag.service';
import { ShapeState } from '../../../../../../../cross-team-shared/annotation/shared/state/shape-state';
import { isShapeVisible$ } from '../../../../../../../cross-team-shared/annotation/shared/utils/keyframe-utils';
import { BrowserControlService } from '../../../../../../../cross-team-shared/misc-services/browser-control.service';
import { assertStore } from '../../../../../../../cross-team-shared/misc-utils';
import { isPointCloudOutput } from '../../../../../../../cross-team-shared/misc-utils/output-utils';
import { settingsStoreToken } from '../../../../../../../cross-team-shared/state/setting-state';
import { EventTrackingService } from '../../../../../../../shared/event-tracking/event-tracking.service';

@Component({
  selector: 'h3-shape-list-item',
  templateUrl: './shape-list-item.component.html',
  styleUrls: [
    '../../object-list/object-list-item/object-list-item.component.scss',
    './shape-list-item.component.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ShapeListItemComponent
  extends DirectiveSuperclass
  implements OnInit
{
  @Input({ required: true }) shape!: ShapeState;
  selected$!: Observable<boolean | undefined>;
  color$!: Observable<string>;
  invisible$!: Observable<boolean>;
  isMissingRequiredTag$!: Observable<boolean>;

  private selectionService = inject(SelectionService);
  private browserControlService = inject(BrowserControlService);
  private fusionStore = inject(fusionStoreToken);
  private taskWorkspaceStore = inject(TaskWorkspaceStore);
  private contextMenuService = inject(ShapeListContextMenuService);
  private outputName!: string;
  private pointCloudOutputName?: string;
  private elementRef = inject(ElementRef);
  private fusionManager = inject(FusionManager);
  private settingsStore = inject(settingsStoreToken);
  private eventTrackingService = inject(EventTrackingService);
  private featureFlagService = inject(FeatureFlagService);

  @HostListener('click', ['$event'])
  async selectShape(event: MouseEvent) {
    if (event[this.browserControlService.getPlatformModifier()]) {
      this.selectionService.toggleShapeSelection([this.shape.index], {
        source: 'shape_list',
      });
    } else {
      this.selectionService.setSelection([], [this.shape.index], {
        source: 'shape_list',
      });

      if (this.featureFlagService.hasFlag('change_output_single_click')) {
        this.switchOutput();
      }
    }
  }

  @HostListener('dblclick', ['$event'])
  async handleDoubleClick(_event: MouseEvent) {
    if (!this.featureFlagService.hasFlag('change_output_single_click')) {
      this.switchOutput();
    }
    if (!this.settingsStore.state().inspectMode) {
      return;
    }

    await firstValueFrom(timer(0));

    return this.fusionManager
      .getContentConnector(this.outputName)
      .inspectFocusedShape();
  }

  @HostListener('contextmenu', ['$event'])
  openContextMenu(event: MouseEvent) {
    event.preventDefault();

    if (
      !this.selectionService
        .getSelectedShapeIndexes()
        .some((index) => index === this.shape.index)
    ) {
      this.selectionService.setSelection([], [this.shape.index]);
    }

    return this.contextMenuService.open(
      [event.clientX, event.clientY],
      this.shape.index,
    );
  }

  ngOnInit() {
    this.selected$ = this.taskWorkspaceStore('shapeSelection')(
      this.shape.index,
    ).$;
    assert(
      this.shape.outputName,
      'all shapes in object enabled projects should have an output name',
    );
    this.outputName = this.shape.outputName;

    const shapeTransientInfoStore = this.taskWorkspaceStore(
      'shapeTransientInfo',
    )(this.shape.index);
    assertStore(shapeTransientInfoStore, 'shape must exist');
    this.color$ = shapeTransientInfoStore('color').$;

    this.invisible$ = this.buildIsShapeVisible$();

    const shapeTransientInfo = this.taskWorkspaceStore('shapeTransientInfo')(
      this.shape.index,
    );
    assertStore(shapeTransientInfo, 'shape must have transient info');
    this.isMissingRequiredTag$ = shapeTransientInfo('hasMissingRequiredTags').$;

    const pointCloudOutput = this.taskWorkspaceStore
      .state()
      .project.outputs.find(isPointCloudOutput);
    this.pointCloudOutputName = pointCloudOutput?.name;

    this.setUpScrollIntoViewSubscription();
  }

  private switchOutput() {
    const { selectedOutputName } = this.taskWorkspaceStore.state();
    if (selectedOutputName !== this.outputName) {
      this.taskWorkspaceStore('selectedOutputName').set(this.outputName);
      this.trackActiveOutputChangedEvent('shape_selection');

      if (!this.isInSplitView() || this.is3DShape()) {
        return;
      }

      this.taskWorkspaceStore('splitViewOutputName').set(this.outputName);
    }
  }

  private buildIsShapeVisible$() {
    return isShapeVisible$(
      this.fusionStore('frameNumber'),
      this.taskWorkspaceStore('outputAnnotationInfo')(this.outputName)(
        // @ts-ignore -- fix me
        'shapes',
      )(this.shape.index),
    ).pipe(map((visibility) => visibility === 0));
  }

  private isInSplitView() {
    return isDefined(this.taskWorkspaceStore.state().splitViewOutputName);
  }

  private is3DShape() {
    return this.outputName === this.pointCloudOutputName;
  }

  private setUpScrollIntoViewSubscription() {
    this.subscribeTo(this.selected$, (selected) => {
      if (selected) {
        (this.elementRef.nativeElement as Element).scrollIntoView({
          block: 'nearest',
        });
      }
    });
  }

  private trackActiveOutputChangedEvent(source: string) {
    this.eventTrackingService.track({
      id: 'change_active_output',
      bucket: 'navigation',
      active_output: this.outputName,
      source,
    });
  }
}
